import React, { useRef, useState } from 'react';
import { ArrowLeftIcon, ArrowRightIcon } from './icons';
import { colors } from './theme';

// ── card carousel ────────────────────────────────────────────────────────────
// A swipeable row of cards with an arrow either side and a position marker
// below. Shared by the example cards on a phone and by History.
//
// `renderItem(item)` draws a card, `getKey(item)` keys it, `labelledBy` is the
// id of the heading that names the list, and `itemName` finishes the arrows'
// labels ("Previous example").

// Past this many cards the dots stop saying anything, so it's "3 of 20".
const MAX_DOTS = 7;

const arrowBtn = {
  flex: '0 0 auto',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 44,
  height: 44,
  borderRadius: 999,
  background: 'transparent',
  border: `1px solid ${colors.accentDim}`,
  color: colors.accent,
  cursor: 'pointer',
  transition: 'background 0.15s, color 0.15s',
};

// offsetLeft counts from the page, not the track, so it is measured against the
// first slide. On a wide screen the gap to the track alone is wider than a card.
const slideLeft = (track, slide) =>
  slide.offsetLeft - track.children[0].offsetLeft;

const ArrowButton = ({ label, onClick, children }) => (
  <button
    type="button"
    aria-label={label}
    onClick={onClick}
    style={arrowBtn}
    onMouseEnter={e => {
      e.currentTarget.style.background = colors.accent;
      e.currentTarget.style.color = colors.onAccent;
    }}
    onMouseLeave={e => {
      e.currentTarget.style.background = 'transparent';
      e.currentTarget.style.color = colors.accent;
    }}
  >
    {children}
  </button>
);

const ReaderCarousel = ({
  items,
  getKey,
  renderItem,
  labelledBy,
  itemName,
}) => {
  const trackRef = useRef(null);
  const [rawIndex, setIndex] = useState(0);
  // Deleting the last card leaves the stored position past the end.
  const index = Math.min(rawIndex, Math.max(items.length - 1, 0));

  const go = dir => {
    const next = Math.min(items.length - 1, Math.max(0, index + dir));
    setIndex(next);
    const track = trackRef.current;
    if (track) {
      const slide = track.children[next];
      if (slide) {
        track.scrollTo({ left: slideLeft(track, slide), behavior: 'smooth' });
      }
    }
  };

  // keep the active dot/buttons in sync when the user swipes/scrolls manually.
  // find the slide whose left edge is nearest the current scroll position
  // (robust to the edge-peek where a slide is narrower than the viewport).
  const onScroll = () => {
    const track = trackRef.current;
    if (!track) return;
    let nearest = 0;
    let best = Infinity;
    Array.from(track.children).forEach((child, i) => {
      const dist = Math.abs(slideLeft(track, child) - track.scrollLeft);
      if (dist < best) {
        best = dist;
        nearest = i;
      }
    });
    if (nearest !== index) setIndex(nearest);
  };

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <ArrowButton label={`Previous ${itemName}`} onClick={() => go(-1)}>
          <ArrowLeftIcon style={{ fontSize: 34 }} />
        </ArrowButton>

        <style>{`.reader-carousel-track::-webkit-scrollbar{display:none}`}</style>
        <div
          ref={trackRef}
          onScroll={onScroll}
          role="list"
          aria-labelledby={labelledBy}
          className="reader-carousel-track"
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            gap: 12,
            overflowX: 'auto',
            scrollSnapType: 'x mandatory',
            scrollPadding: '0 4px',
            overscrollBehaviorX: 'contain',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
        >
          {items.map(item => (
            <div
              key={getKey(item)}
              role="listitem"
              style={{
                // edge-peek: a sliver of the next card hints it's swipeable
                flex: '0 0 88%',
                minWidth: '88%',
                scrollSnapAlign: 'start',
                boxSizing: 'border-box',
                padding: '4px 4px 8px',
              }}
            >
              {renderItem(item)}
            </div>
          ))}
        </div>

        <ArrowButton label={`Next ${itemName}`} onClick={() => go(1)}>
          <ArrowRightIcon style={{ fontSize: 34 }} />
        </ArrowButton>
      </div>

      {/* progress dots */}
      {items.length > MAX_DOTS ? (
        <div
          aria-hidden="true"
          data-testid="carousel-position"
          style={{
            textAlign: 'center',
            marginTop: 14,
            color: colors.textDim,
            fontSize: 13,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {index + 1} of {items.length}
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: 8,
            marginTop: 14,
          }}
        >
          {items.map((item, i) => (
            <span
              key={getKey(item)}
              aria-hidden="true"
              data-testid="carousel-dot"
              style={{
                width: i === index ? 20 : 8,
                height: 8,
                borderRadius: 999,
                background: i === index ? colors.accent : colors.border,
                transition: 'width 0.2s, background 0.2s',
              }}
            />
          ))}
        </div>
      )}
    </>
  );
};

export default ReaderCarousel;
